// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.

// Import bootstrap css
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

import Vue from 'vue';
import { mapActions } from 'vuex';
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';
import moment from 'moment';
import axios from 'axios';
import VueTour from 'vue-tour';
import VueSidebarMenu from 'vue-sidebar-menu';
import ToggleButton from 'vue-js-toggle-button';

import 'vue-tour/dist/vue-tour.css';

import App from './App';
import router from './router';
import store from './store';

import {
  APIBASEURL, APIMETADATA, APICABLES, APIINSTRUMENTS,
  APIAREAS, APIINFRASTRUCTURES, APIPARAMETERS, APISITES, APIGLOBALRANGES, APICATALOG,
  APIDATA, DATAPORTALPOSTS, APIINSTSTATUS, APIINFRASTATUS, APIMAPBOXSPRITE,
} from './utils/constants';

import './assets/style.scss';

Vue.use(BootstrapVue); // letting project styling override BootstrapVue styling
Vue.use(BootstrapVueIcons);
Vue.use(VueSidebarMenu); // letting project use sidebar menu component
Vue.use(ToggleButton); // letting project use switch toggle button components
Vue.use(VueTour);

Vue.config.productionTip = false;

const isProd = process.env.NODE_ENV === 'production';

window.dataLayer = window.dataLayer || [];
Vue.prototype.$ga = function () { dataLayer.push(arguments); }; // eslint-disable-line

/*
  Below is the initial set up of the data portal application
  Load in all the sites, infrastructures, instruments, map, cable
  sites, and time. The loading screen prgress bar is updated below
  as well.
*/
new Vue({ // eslint-disable-line no-new
  el: '#app',
  store,
  router,
  data: {
    localtime: moment(),
    instruments: null,
    sites: null,
    cables: null,
    map: null,
    version: 2,
  },
  computed: {
    currentTime() { // calculated the UTC date
      return this.localtime.format();
    },
  },

  // Getters for the data portal, using progress
  methods: {
    ...mapActions([
      'storeCables',
      'storeInstruments',
      'storeAreas',
      'storeSites',
      'storeInfrastructures',
      'storeGlobalRanges',
      'storeInstrumentStatus',
      'storeInfrastructureStatus',
      'storeInstrumentsCatalog',
      'storeSpriteIcons',
      'storeDataPortalPosts',
      'storeParameters',
      'updateProgress']),
    // createWebWorker
    getCables() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APICABLES}`);
    },
    getInstruments() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIINSTRUMENTS}`);
    },
    getAreas() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIAREAS}`);
    },
    getParameters() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIPARAMETERS}`);
    },
    getInfras() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIINFRASTRUCTURES}`);
    },
    getSites() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APISITES}`);
    },
    getPosts() {
      return axios.get(`${DATAPORTALPOSTS}`);
    },
    getGlobalRanges() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIGLOBALRANGES}`);
    },
    getStatusIndicators() {
      return axios.get(`${APIINSTSTATUS}`);
    },
    getInfraIndicators() {
      return axios.get(`${APIINFRASTATUS}`);
    },
    getInstrumentsCatalog() {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APICATALOG}`);
    },
    getSprites() {
      return axios.get(`${APIMAPBOXSPRITE}`);
    },
    async getSupportingMeta() {
      try {
        const spritesResponse = await this.getSprites();
        this.storeSpriteIcons({ spriteIcons: spritesResponse.data });

        const instCatalogResponse = await this.getInstrumentsCatalog();
        this.storeInstrumentsCatalog({ instrumentsCatalog: instCatalogResponse.data });

        const globalRangesResponse = await this.getGlobalRanges();
        this.storeGlobalRanges({
          globalRanges: globalRangesResponse.data,
        }); // Sets the global range information to the store

        const statusIndicatorsResponse = await this.getStatusIndicators();
        this.storeInstrumentStatus({ statusIndicators: statusIndicatorsResponse.data });

        const infraIndicatorsResponse = await this.getInfraIndicators();
        this.storeInfrastructureStatus({ infraIndicators: infraIndicatorsResponse.data });
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.$store.commit('appNotReady');
      }
    },
    async getData() {
      try {
        const instrumentsResponse = await this.getInstruments();
        this.storeInstruments({
          instruments: instrumentsResponse.data,
        }); // sets instrument information in store
        this.updateProgress({ val: 20 }); // adds to progress bar

        const cablesResponse = await this.getCables();
        this.storeCables({ cables: cablesResponse.data }); // sets cable information in store
        this.updateProgress({ val: 20 }); // adds to progress bar

        const sitesResponse = await this.getSites();
        this.storeSites({ sites: sitesResponse.data }); // sets site information in store
        this.updateProgress({ val: 10 }); // adds to progress bar

        const parametersResponse = await this.getParameters();
        this.storeParameters({
          parameters: parametersResponse.data,
        }); // sets parameter information in store
        this.updateProgress({ val: 10 }); // adds to progress bar

        const areasResponse = await this.getAreas();
        this.storeAreas({ areas: areasResponse.data }); // sets areas information in store
        this.updateProgress({ val: 20 }); // adds to progress bar

        const infrasResponse = await this.getInfras();
        this.storeInfrastructures({
          infras: infrasResponse.data,
        }); // sets infras information in store
        this.updateProgress({ val: 10 }); // adds to progress bar

        const postsResponse = await this.getPosts();
        this.storeDataPortalPosts({
          dataPortalPosts: postsResponse.data,
        }); // sets posts information in store
        this.updateProgress({ val: 10 }); // adds to progress bar

        setTimeout(() => { // add 4 secs to buffer on loading screen
          this.$store.commit('appReady');
        }, 150);
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.$store.commit('appNotReady');
      }
    },

  },
  // Grabs the data and parameters, sets the GA gtag to its specfic config ID
  created() {
    this.$ga('js', new Date());
    this.$ga('config', process.env.GA_ID, { 'debug_mode': !isProd }); // eslint-disable-line
    this.getSupportingMeta();
    this.getData();
    // this.createWebWorker();
  },
  components: { App }, // sets all this to the main element, App
  template: '<App/>',
});

export {
  APIBASEURL,
  APIDATA,
  APIINSTRUMENTS,
  APIINFRASTRUCTURES,
  APIPARAMETERS,
  APISITES,
  APICABLES,
  APIGLOBALRANGES,
  DATAPORTALPOSTS,
  APIINSTSTATUS,
  APIINFRASTATUS,
  APIMAPBOXSPRITE,
};
